import type { FC } from "react"
import React, { useEffect, useState } from "react"
import { FormControl, FormLabel, HStack, Text } from "@chakra-ui/react"
import type { FieldProps } from "formik"
import { Field, useFormikContext } from "formik"
import type { AbiInput } from "web3-utils"

import CreateProposalFormError from "createProposal/components/CreateProposalFormError"
import type { CreateProposalFormValues } from "createProposal/types/form"
import SimpleValueInput from "createProposal/components/actions/SimpleValueInput"
import { encodeComplexField } from "createProposal/helpers/abi"
type CreateProposalCustomInputProps = {
  label: string
  name: string
  placeholder: string
  validate?: (value?: string) => string | undefined
  components: AbiInput[]
}

const INITIAL_VALUE = ""

const CreateProposalTupleInput: FC<CreateProposalCustomInputProps> = ({
  label,
  name,
  placeholder,
  validate,
  components,
}) => {
  const { setFieldValue } = useFormikContext<CreateProposalFormValues>()

  const [invalidCombinedValue, setInvalidCombinedValue] = useState(false)
  const [touchedField, setTouchedField] = useState<boolean>(false)

  const [combinedValue, setCombinedValue] = useState(INITIAL_VALUE)
  useEffect(() => {
    try {
      encodeComplexField(components, "tuple", combinedValue)
      setInvalidCombinedValue(false)
    } catch (e) {
      setInvalidCombinedValue(true)
    }
  }, [combinedValue, components])

  return (
    <Field name={name} validate={validate}>
      {({ field }: FieldProps) => {
        const { name } = field

        const handleChangeValue = (newValue: string) => {
          setTouchedField(true)
          setCombinedValue(newValue)
          setFieldValue(name, newValue, true)
        }

        const isInvalid = touchedField && invalidCombinedValue // should be using Boolean(meta.error) but due to TALLY-5643 we use an internal validation

        return (
          <FormControl isInvalid={isInvalid}>
            <HStack h={10}>
              <FormLabel
                alignItems="center"
                bg="purple.50"
                border="gray.dark"
                color="purple.600"
                display="flex"
                h="full"
                justifyContent="center"
                m={0}
                p={1}
                w={220}
              >
                <Text textStyle="body.bold.lg" w="auto">
                  {label}
                </Text>
              </FormLabel>
              <Text width="101.5%">{placeholder}</Text>
            </HStack>
            <SimpleValueInput
              changeInputValue={handleChangeValue}
              components={components}
            />

            <CreateProposalFormError
              error={
                isInvalid
                  ? "Please fill all the tuple fields correctly"
                  : undefined
              }
            />
          </FormControl>
        )
      }}
    </Field>
  )
}

export default CreateProposalTupleInput
